import { useMapRelativePositionToCameraPosition } from '@sketch-hq/sketch-web-renderer'
import { useCallback } from 'react'
import { InspectorLayersTreeNode, Point } from './types'

export type CanHandleEventPredicate = (node: InspectorLayersTreeNode) => boolean

/**
 * Hook returning a function that can be used to find the layer node behind a given point
 * in the "screen" space (typically extracted from a mouse event relative the canvas HTML element).
 */
export function useFindLayerNodeFromCanvasRelativePositionInScreenSpace(
  rootNode: InspectorLayersTreeNode | null
) {
  const mapRelativePositionToCameraPosition = useMapRelativePositionToCameraPosition()

  return useCallback(
    (
      canvasRelativePositionInScreenSpace: Point,
      /**
       * Sometimes we want to conditionally allow user to interact with a certain layer.
       * When provided, if this function returns false, the event system will ignore the layer
       * and continue looking for target.
       */
      canHandleEventPredicate?: CanHandleEventPredicate
    ) => {
      if (!rootNode) {
        return null
      }

      const canvasRelativePositionInDocumentSpace = mapRelativePositionToCameraPosition(
        canvasRelativePositionInScreenSpace.x,
        canvasRelativePositionInScreenSpace.y
      )

      if (!canvasRelativePositionInDocumentSpace) {
        return null
      }

      return findLayerNodeFromPositionInDocumentSpace(
        rootNode,
        canvasRelativePositionInDocumentSpace,
        canHandleEventPredicate
      )
    },
    [rootNode, mapRelativePositionToCameraPosition]
  )
}

/**
 * Find the layer containing the given point in the document space
 * (Using Sketch coordinates, not screen coordinates)
 */
function findLayerNodeFromPositionInDocumentSpace(
  node: InspectorLayersTreeNode,
  canvasRelativePositionInDocumentSpace: Point,
  canHandleEventPredicate: CanHandleEventPredicate | undefined
): InspectorLayersTreeNode | null {
  const currentBounds = node.prMarinaNode.getAbsoluteBounds()
  const containsPosition = currentBounds.contains(
    canvasRelativePositionInDocumentSpace
  )

  if (!containsPosition) {
    return null
  }

  for (const childNode of node.children) {
    const matchingNode = findLayerNodeFromPositionInDocumentSpace(
      childNode,
      canvasRelativePositionInDocumentSpace,
      canHandleEventPredicate
    )

    // Descendants have priority
    // if we found a descendant that contains the position, return it
    if (matchingNode && canHandleEventPredicate?.(matchingNode)) {
      return matchingNode
    }
  }

  if (canHandleEventPredicate?.(node)) {
    return node
  }

  return null
}
